import { convert } from "@/libs/units";
import { getSimplifiedRequiredBhp } from "../tornado";
import type { SinglePumpChartContext } from "..";
import type { DataSet, NumericPoint } from "@incytestudios/ddv";
import { isFinitePoint, isNaturalPoint } from "../_common";
import { PoundsPerCubicFoot } from "@/libs/units/density";
import { getDensityCorrectedProducedPoints } from "../_common/densityCorrection";

export const getBrakeHPPoints = (
  context: SinglePumpChartContext,
  targetDensityCorrectionRatio: number
) => {
  const {
    hypotheticals: { hypotheticalFlowRates },
    ascendingOrderPumps,
    pumpWearBHPCorrectionRatio,
    // fluidDensitySpGr,
    dischargeFluidDensityLBMFT3,
    frequencyRatio
  } = context;

  let brakeHpPoints: NumericPoint[] = [];
  let brakeHpPointsRateCorrectedPumpIds: string[] = [];
  const fluidDensitySpGr = PoundsPerCubicFoot.toStandardUnit(
    dischargeFluidDensityLBMFT3
  );
  // Generate brake HP points for each hypothetical flow rate
  // with the simplified required bhp calculation
  // Returns two separate datasets: x/y points and corrected pumps
  const brakeHpPointsData = hypotheticalFlowRates.map(
    (flowRateStbD: number) => {
      const data = getSimplifiedRequiredBhp(
        flowRateStbD,
        frequencyRatio,
        ascendingOrderPumps,
        fluidDensitySpGr,
        pumpWearBHPCorrectionRatio
      );

      const { bhp, rateCorrectedPumps } = data;
      return {
        points: {
          x: flowRateStbD,
          y: bhp
        },
        correctedPumps: rateCorrectedPumps
      };
    }
  );
  // Extract the x/y points
  brakeHpPoints = brakeHpPointsData.map<NumericPoint>(data => data.points);

  const densityAdjustedBHPPoints = getDensityCorrectedProducedPoints(
    brakeHpPoints,
    targetDensityCorrectionRatio
  );
  // Get a list of all rate corrected pumps used in the brake hp points
  brakeHpPointsRateCorrectedPumpIds = [
    ...new Set(
      brakeHpPointsData.map(d => d.correctedPumps.map(pump => pump.id)).flat()
    )
  ];

  return {
    brakeHpPoints: densityAdjustedBHPPoints,
    brakeHpPointsRateCorrectedPumpIds
  };
};
export const getDataSetBrakeHP = (
  context: SinglePumpChartContext,
  targetDensityCorrectionRatio: number
): DataSet & {
  rateReducedPumpIds: string[];
  originalPoints: NumericPoint[];
} => {
  const {
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseBhpYUnit,
    targetBhpYUnit,
    targetBhpYUnitLabel
  } = context;
  const data = getBrakeHPPoints(context, targetDensityCorrectionRatio);
  const convertedBrakeHpPoints = data.brakeHpPoints
    .map<NumericPoint>(pt => ({
      x: convert(pt.x, baseXUnit, targetXUnit),
      y: convert(pt.y, baseBhpYUnit, targetBhpYUnit)
    }))
    .filter(isFinitePoint)
    .filter(isNaturalPoint);
  return {
    title: "BHP",
    xAxisLabel: `In Situ Flow Rate (${targetXUnitLabel})`,
    yAxisLabel: `HP Required (${targetBhpYUnitLabel})`,
    points: convertedBrakeHpPoints,
    originalPoints: data.brakeHpPoints,
    rateReducedPumpIds: data.brakeHpPointsRateCorrectedPumpIds
  };
};
